import { Component, OnDestroy, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AdyenService } from '../../services/adyen.service';
import { OnlinePaymentService } from '../../services/online-payment.service';
import { PaymentEventsService } from '../../services/payment-events.service';
import { DataService } from '../../services/data.service';
import { AlertDialogComponent } from '../../shared/alert-dialog/alert-dialog.component';
import { MastercardPayload } from '../../models/payment.model';
import { PayAtTableNiPayload } from '../../models/pay-at-table.model';

@Component({
  selector: 'app-adyen',
  templateUrl: './adyen.component.html',
  styleUrls: ['./adyen.component.scss'],
  standalone: false
})
export class AdyenComponent implements OnInit, OnDestroy {

  public loading = true;
  public readonly dropinContainerId = 'adyen-dropin-container';

  private payload: MastercardPayload | PayAtTableNiPayload | null = null;
  private sessionId: string | null = null;
  private transactionId: string | null = null;
  private chequeId: string | null = null;

  constructor(
    private adyenService: AdyenService,
    private onlinePaymentService: OnlinePaymentService,
    private paymentEventsService: PaymentEventsService,
    private dataService: DataService,
    private dialog: MatDialog,
    private router: Router,
    private location: Location,
  ) { }

  public ngOnInit(): void {
    const payload = this.onlinePaymentService.consumePendingPayload();

    if (!payload) {
      this.location.back();
      return;
    }

    this.payload = payload;
    this.chequeId = 'cheque_id' in payload ? payload.cheque_id ?? null : null;

    this.startSession();
  }

  private startSession(): void {
    if (!this.payload) {
      return;
    }

    this.loading = true;
    this.adyenService.getSessionDetails(this.payload).subscribe({
      next: (sessionDetails) => {
        this.sessionId = sessionDetails.session.id;
        this.transactionId = sessionDetails.transactionId;
        this.adyenService.mount(this.dropinContainerId, sessionDetails, {
          onPaymentCompleted: (resultCode, sessionResult) => this.handlePaymentResult(resultCode, sessionResult),
          onPaymentFailed: (resultCode, sessionResult) => this.handlePaymentResult(resultCode, sessionResult),
          onError: () => this.handleFatalError(),
        }).then(() => this.loading = false);
      },
      // The session itself couldn't be created (backend/network down) — retrying
      // immediately would just loop, so stop and let the shopper back out instead.
      error: () => this.handleFatalError(),
    });
  }

  public ngOnDestroy(): void {
    this.adyenService.unmount();
  }

  // Bound to the top-bar's customBack input.
  public cancel = (): void => {
    this.paymentEventsService.notifyPaymentCancelled();
    this.location.back();
  };

  private handlePaymentResult(resultCode: string, sessionResult: string | null): void {
    if (!this.sessionId || !this.transactionId) {
      // Mount succeeded but our own session state is missing — an internal bug,
      // not a card decline. Don't retry into the same broken state.
      this.handleFatalError();
      return;
    }

    if (resultCode !== 'Authorised') {
      // A genuine decline: nothing was charged, so it's safe to let the shopper
      // try again with a fresh session.
      this.handleDeclinedPayment();
      return;
    }

    this.adyenService.validateAdyenSession({
      sessionId: this.sessionId,
      transactionId: this.transactionId,
      sessionResult,
      chequeId: this.chequeId,
    }).subscribe((validated) => {
      if (!validated) {
        this.handleFatalError();
        return;
      }
      this.dataService.transactionLoaded.set(false);
      this.dialog.closeAll();
      this.router.navigate(['/bill-paid'], {
        queryParams: { table_id: this.dataService.table()?.id, transaction_id: this.transactionId },
      });
    });
  }

  private handleDeclinedPayment(): void {
    this.paymentEventsService.notifyPaymentCancelled();
    AlertDialogComponent.open(this.dialog, {
      type: 'error', title: 'PAYMENT.TITLE', message: 'PAYMENT.ERROR', enableCancelBtn: false,
    });
    this.adyenService.unmount();
    this.startSession();
  }

  private handleFatalError(): void {
    this.paymentEventsService.notifyPaymentCancelled();
    AlertDialogComponent.open(this.dialog, {
      type: 'error', title: 'PAYMENT.TITLE', message: 'PAYMENT.ERROR', enableCancelBtn: false,
    });
    this.adyenService.unmount();
    this.location.back();
  }

}
